import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, input, output, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';

import { ToastMessageService } from '@fitness-force/api';
import { ConfirmationService } from 'primeng/api';

import { SupplierFormComponent } from '../supplier-form/supplier-form.component';
import { SupplierResponse, ListSuppliersResponse, UpdateSupplierRequest } from '../../models';
import { SupplierService } from '../../services';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import type { TENANTSINTERFACE } from '@fitness-force/models';

@Component({
  selector: 'lib-supplier-list',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    SupplierFormComponent
  ],
  providers: [ConfirmationService],
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '900px', height: '80vh' }"
      (onHide)="close()">

      <ng-template pTemplate="header">
        <h3>{{ 'RETAIL_BILLING.SUPPLIER.MANAGE' | translate }}</h3>
      </ng-template>

      <div class="card">
        <div class="flex justify-content-between align-items-center mb-4">
          <p-iconField iconPosition="left">
            <p-inputIcon styleClass="pi pi-search" />
            <input
              pInputText
              type="text"
              [placeholder]="'common.PLACEHOLDER.SEARCH' | translate"
              [(ngModel)]="searchTerm"
              (ngModelChange)="onSearch()"
              aria-label="search-suppliers-input" />
          </p-iconField>

          <p-button [label]="'RETAIL_BILLING.SUPPLIER.CREATE' | translate"
            icon="pi pi-plus"
            (onClick)="showCreateDialog()"
            aria-label="create-supplier-button" />
        </div>

        <p-table
          [value]="suppliers"
          [loading]="loading"
          [paginator]="true"
          [lazy]="true"
          [rows]="pageSize"
          [(first)]="first"
          [totalRecords]="totalRecords"
          (onPage)="onPageChange($event)"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          [rowsPerPageOptions]="[10, 25, 50]"
          paginatorDropdownAppendTo="body"
          styleClass="p-datatable-gridlines">

          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'RETAIL_BILLING.SUPPLIER.NAME' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.SUPPLIER.EMAIL' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.SUPPLIER.PHONE' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.PRODUCTS.STATUS' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.COMMON.ACTIONS' | translate }}</th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-supplier>
            <tr>
              <td>
                <div>
                  <div class="font-medium">{{ supplier.name_primary_language }}</div>
                  <div class="text-sm text-500">{{ supplier.name_secondary_language }}</div>
                </div>
              </td>
              <td>{{ supplier.email }}</td>
              <td>{{ supplier.phone }}</td>
              <td>
                <p-tag [value]="supplier.is_active ? ('RETAIL_BILLING.PRODUCTS.ACTIVE' | translate) : ('RETAIL_BILLING.PRODUCTS.INACTIVE' | translate)"
                  [severity]="supplier.is_active ? 'success' : 'danger'" />
              </td>
              <td>
                <div class="flex gap-2">
                  <p-button [icon]="supplier.is_active ? 'pi pi-check-circle' : 'pi pi-times-circle'"
                    [pTooltip]="supplier.is_active ? ('RETAIL_BILLING.COMMON.MARK_INACTIVE' | translate) : ('RETAIL_BILLING.COMMON.MARK_ACTIVE' | translate)"
                    tooltipPosition="top"
                    [text]="true"
                    [rounded]="true"
                    [outlined]="true"
                    size="small"
                    [severity]="supplier.is_active ? 'success' : 'danger'"
                    (onClick)="toggleSupplierStatus(supplier)"
                    [ariaLabel]="'toggle-status-supplier-' + supplier.id" />
                  <p-button icon="pi pi-pencil"
                    [pTooltip]="'RETAIL_BILLING.COMMON.EDIT' | translate"
                    tooltipPosition="top"
                    [text]="true"
                    [rounded]="true"
                    [outlined]="true"
                    size="small"
                    (onClick)="editSupplier(supplier)"
                    [ariaLabel]="'edit-supplier-' + supplier.id" />
                </div>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="5" class="text-center">{{ 'RETAIL_BILLING.SUPPLIER.NO_SUPPLIERS' | translate }}</td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex justify-content-end">
          <p-button [label]="'RETAIL_BILLING.COMMON.CLOSE' | translate"
            icon="pi pi-times"
            [text]="true"
            (onClick)="close()"
            aria-label="close-button" />
        </div>
      </ng-template>
    </p-dialog>

    @if (showSupplierDialog) {
      <lib-supplier-form [visible]="showSupplierDialog"
        [supplier]="selectedSupplier"
        [companyLevel]="companyLevel()"
        [tenantId]="tenantId()"
        [primaryLanguage]="primaryLanguage()"
        [secondaryLanguage]="secondaryLanguage()"
        [isSecondaryLanguage]="isSecondaryLanguage()"
        (onSave)="onSupplierSaved($event)"
        (onCancel)="hideSupplierDialog()" />
    }

    <p-confirmDialog />
    <p-toast />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SupplierListComponent implements OnInit, OnDestroy {
  private readonly supplierService = inject(SupplierService);
  private readonly toastService = inject(ToastMessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);

  readonly visible = input(false);
  readonly companyLevel = input<boolean>(true);
  readonly tenantId = input<number>();
  readonly primaryLanguage = input<string>('');
  readonly secondaryLanguage = input<string>('');
  readonly isSecondaryLanguage = input<boolean>(false);
  readonly visibleChange = output<boolean>();
  readonly onClose = output<void>();

  suppliers: SupplierResponse[] = [];
  loading = false;
  totalRecords = 0;
  pageSize = 10;
  pageNumber = 1;
  searchTerm = '';
  first = 0;
  showSupplierDialog = false;
  selectedSupplier: SupplierResponse | null = null;

  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  ngOnInit() {
    this.searchSubject$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageNumber = 1;
        this.first = 0;
        this.loadSuppliers();
      });

    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenant: TENANTSINTERFACE) => {
        if (tenant.selected_tenant.id > 0) {
          this.store.dispatch({ type: 'ROUTER_GO' });
          this.loadSuppliers();
        }
      });
  }

  loadSuppliers() {
    this.loading = true;
    this.supplierService.getCompanySuppliers({
      page: this.pageNumber,
      page_size: this.pageSize,
      search_term: this.searchTerm || undefined,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ListSuppliersResponse) => {
          this.suppliers = response.items || [];
          this.totalRecords = response.total_items;
          if (response.page_size) {
            this.pageSize = response.page_size;
          }
          if (response.current_page) {
            this.pageNumber = response.current_page;
            this.first = (response.current_page - 1) * this.pageSize;
          }
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this.toastService.showError(error);
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  onSearch() {
    this.searchSubject$.next(this.searchTerm);
  }

  onPageChange(e: { first: number; rows: number }) {
    this.pageSize = e.rows;
    this.first = e.first;
    this.pageNumber = e.first ? e.first / this.pageSize + 1 : 1;
    this.loadSuppliers();
  }

  showCreateDialog() {
    this.selectedSupplier = null;
    this.showSupplierDialog = true;
  }

  editSupplier(supplier: SupplierResponse) {
    this.selectedSupplier = supplier;
    this.showSupplierDialog = true;
  }

  toggleSupplierStatus(supplier: SupplierResponse) {
    const newStatus = !supplier.is_active;

    const updateSupplierRequest: UpdateSupplierRequest = {
        name_primary_language: supplier.name_primary_language,
        name_secondary_language: supplier.name_secondary_language,
        description_primary_language: supplier.description_primary_language,
        description_secondary_language: supplier.description_secondary_language,
        email: supplier.email,
        phone: supplier.phone,
        address: supplier.address,
        is_active: newStatus
    }
    const confirmMessage = newStatus
      ? this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_ACTIVATE')
      : this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_DEACTIVATE');

    this.confirmationService.confirm({
      message: confirmMessage,
      header: this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.supplierService.updateCompanySupplier(supplier.id, updateSupplierRequest)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              const successMessage = newStatus
                ? this.translate.instant('RETAIL_BILLING.SUPPLIER.ACTIVATED_SUCCESS')
                : this.translate.instant('RETAIL_BILLING.SUPPLIER.DEACTIVATED_SUCCESS');
              this.toastService.showSuccess(successMessage);
              this.loadSuppliers();
            },
            error: (error) => {
              this.toastService.showError(error);
            }
          });
      }
    });
  }

  onSupplierSaved(_supplier: SupplierResponse) {
    this.hideSupplierDialog();
    this.loadSuppliers();
    const message = this.selectedSupplier
      ? 'RETAIL_BILLING.SUPPLIER.UPDATE_SUCCESS'
      : 'RETAIL_BILLING.SUPPLIER.CREATE_SUCCESS';
    this.toastService.showSuccess(this.translate.instant(message));
  }

  hideSupplierDialog() {
    this.showSupplierDialog = false;
    this.selectedSupplier = null;
  }

  close(): void {
    this.onClose.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.visibleChange.emit(false);
  }
}
